/**
 * Subastas activas en la ficha de producto (ADR 003).
 *
 * Cuando hay una subasta viva de la MISMA carta —aunque sea otra impresión—
 * este módulo la muestra con su puja actual, un porcentaje comparativo y un
 * botón para pujar. CardVault resuelve el match y el porcentaje; aquí solo se
 * pinta y se decide a dónde mandar al comprador.
 *
 * Tres cosas que parecen detalle y son el diseño:
 *
 * - **No hay polling.** Lectura al cargar, countdown local, y UNA sola
 *   re-validación: al abrir el modal de puja. Nadie mira una ficha esperando
 *   que suba una puja; la obsolescencia solo tiene consecuencia en el instante
 *   en que alguien decide pujar.
 * - **El contador nunca afirma que cerró.** El anti-snipe extiende el cierre
 *   2 minutos por puja tardía, así que el reloj local puede llegar a cero con
 *   la subasta viva. Al llegar a cero dice "cerrando…", nunca "finalizada":
 *   afirmar el cierre de algo que se extendió es el único error visible que
 *   este módulo puede cometer.
 * - **Se mide el embudo, no el tráfico.** Tres eventos (ADR 150):
 *   `pdp_auction_view` cuando el módulo se pinta, `pdp_auction_click` cuando se
 *   toca el botón —partido en `pujar` y `subasta`, que son dos cosas distintas—
 *   y `pdp_auction_bid` cuando la puja se registra sin salir de la ficha. NO se
 *   emite nada cuando no hay subasta: sería un evento por vista de ficha en un
 *   catálogo de miles para registrar que no pasó nada.
 * - **El módulo dice si está roto.** El estado vacío es legítimo y frecuente,
 *   así que "roto" y "funcionando bien" renderizan idéntico. Sin una señal
 *   explícita no existe observación que los separe — que es exactamente cómo
 *   el widget del mega menú llevaba desplegado sin funcionar nunca. De ahí
 *   `data-vault-auctions-state` y el `console.warn`. Al comprador NUNCA se le
 *   muestra el error: es una función opcional sobre la que no puede accionar.
 */
(function () {
  'use strict';

  var APP = 'https://cards.the-vault.mx';

  /**
   * Una promesa por producto. Dawn reemplaza la zona de `buy_buttons` entera al
   * cambiar de variante, y el módulo se vuelve a montar; el match es por NOMBRE
   * de carta, que no cambia entre variantes del mismo producto, así que
   * re-consultar sería pagar dos veces por la misma respuesta.
   */
  var porProducto = {};

  /**
   * Lo mismo para el precheck, por subasta. Cada swap de variante re-monta el
   * módulo, y sin esto cada uno gastaría una petición AUTENTICADA para volver a
   * pintar el mismo botón. La frescura no se pierde: el diseño no confía en
   * este valor para pujar — el modal revalida contra `can-bid` antes de dejar
   * escribir un monto, y ese es el único momento en que importa.
   */
  var elegibilidadPorSubasta = {};

  /**
   * Eventos de producto (ADR 150). El helper lo carga `sections/header.liquid`,
   * que renderiza en toda la tienda, pero se comprueba igual: medir nunca puede
   * tumbar la pantalla. Él solo se calla fuera de `the-vault.mx`, así que el
   * servidor de desarrollo no ensucia las métricas de producción.
   */
  function medir(evento, destino) {
    if (typeof window.vaultEvento !== 'function') return;
    window.vaultEvento(evento, destino === undefined ? null : String(destino));
  }

  /**
   * Las vistas ya reportadas en esta carga de página.
   *
   * Dawn reemplaza la zona de `buy_buttons` entera al cambiar de variante y el
   * módulo se vuelve a montar. Sin esto, pasearse por las condiciones de una
   * carta inflaría el denominador con una vista por swap y la tasa de clic
   * saldría artificialmente baja.
   */
  var vistasReportadas = {};

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }

  function money(value) {
    var amount = Number(value);
    if (!isFinite(amount)) return '';
    return '$' + amount.toLocaleString('es-MX');
  }

  /** "2d 4h" · "6h 12m" · "12m 30s" · "cerrando…" */
  function restante(endDate) {
    var ms = new Date(endDate).getTime() - Date.now();
    // Cero NO es "finalizada": el anti-snipe pudo haber extendido el cierre.
    if (!isFinite(ms) || ms <= 0) return 'cerrando…';

    var s = Math.floor(ms / 1000);
    var d = Math.floor(s / 86400);
    var h = Math.floor((s % 86400) / 3600);
    var m = Math.floor((s % 3600) / 60);
    var seg = s % 60;

    if (d > 0) return d + 'd ' + h + 'h';
    if (h > 0) return h + 'h ' + m + 'm';
    if (m > 0) return m + 'm ' + seg + 's';
    return seg + 's';
  }

  /**
   * Qué tan abajo va la puja respecto al precio de lista, o `null` si no se
   * pinta. Se calla cuando no hay ancla y cuando la puja ya la rebasó — una
   * subasta que superó el catálogo no tiene nada que presumir.
   *
   * Nunca se le llama "ahorro" ni "descuento": es un número que SUBE, y un
   * delta presentado como descuento que crece 40% en tres horas quema la
   * confianza en todo el módulo.
   */
  function porcentajeDebajo(puja, lista) {
    var p = Number(puja);
    var l = Number(lista);
    if (!isFinite(p) || !isFinite(l) || l <= 0) return null;
    if (p >= l) return null;
    var pct = Math.round((1 - p / l) * 100);
    return pct > 0 ? pct : null;
  }

  /**
   * La línea que identifica la carta subastada.
   *
   * Se arma con `card_name` —el `cards.name` limpio— y los chips cortos. NO con
   * `title`: ese se compone al crear la subasta como
   * `nombre · expansion_name · condition [· Foil]`, así que pegarle encima la
   * edición y la condición las decía dos veces, una en largo y otra en corto:
   * "Mox Jasper · Tarkir: Dragonstorm · Near Mint · TDM · NM". Era el 100% de
   * las subastas, porque todas las que este módulo muestra se crean por ese
   * mismo camino.
   *
   * Sin `card_name` se cae al título entero y SIN chips: largo, pero no repite.
   * Es la respuesta correcta mientras viva una respuesta cacheada de antes del
   * despliegue de CardVault.
   */
  function lineaDeCarta(subasta) {
    if (!subasta.card_name) return subasta.title;
    var partes = [subasta.card_name];
    if (subasta.expansion_code) partes.push(subasta.expansion_code);
    if (subasta.condition) partes.push(subasta.condition);
    if (subasta.foil) partes.push('Foil');
    return partes.join(' · ');
  }

  /**
   * La miniatura de la carta (ADR 004 §1, sobre el ADR 125 de CardVault).
   *
   * No es decoración: como el match es por NOMBRE y no por impresión, lo que se
   * ofrece puede ser otra edición de la carta que el comprador está viendo. El
   * arte es lo único que se lo dice de un vistazo.
   *
   * Las dos URLs vienen ya resueltas en el payload (`image.primary` y
   * `image.fallback`, ADR 201 §3). El `onerror` hace el swap una sola vez: la
   * primaria puede ser una URL de Shopify cuya imagen ya se archivó, y sin el
   * respaldo el renglón se queda con el hueco. Si no hay ninguna —subastas
   * viejas, creadas antes de que se guardaran— no se pinta nada y el módulo se
   * ve como se veía antes.
   */
  function miniatura(subasta) {
    var imagen = subasta.image || {};
    if (!imagen.primary) return null;

    var img = document.createElement('img');
    img.className = 'vault-subastas__miniatura';
    img.src = imagen.primary;
    img.alt = subasta.card_name || subasta.title || 'Carta en subasta';
    img.loading = 'lazy';
    img.decoding = 'async';
    if (imagen.fallback) {
      img.addEventListener('error', function alRomperse() {
        img.removeEventListener('error', alRomperse);
        img.src = imagen.fallback;
      });
    }
    return img;
  }

  function marcar(mount, estado) {
    mount.setAttribute('data-vault-auctions-state', estado);
  }

  /** La señal es para quien depura. El comprador no ve nada. */
  function avisar(endpoint, detalle) {
    if (window.console && console.warn) {
      console.warn('[vault-subastas] falló ' + endpoint + ' — ' + detalle);
    }
  }

  function pedirJson(url, opciones) {
    return fetch(url, opciones).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.json();
    });
  }

  /**
   * `titulo` es el respaldo para la ficha SIN STOCK.
   *
   * La API resuelve el nombre de la carta contra su propio inventario, así que
   * una ficha de la que nadie tiene copia no tenía nombre que cruzar y devolvía
   * vacío antes de comparar nada — justo donde una subasta de otra impresión es
   * más útil, porque comprar no es opción. El tema sí sabe el título, y el
   * Liquid solo lo pone en singles de Magic.
   *
   * Va como respaldo y no como fuente: si la carta está en el inventario, manda
   * el nombre de la base, que ya viene normalizado. Este es el plan B.
   */
  function subastasDe(productId, titulo) {
    if (!porProducto[productId]) {
      var endpoint = APP + '/api/auctions/by-product?product_id=' + encodeURIComponent(productId);
      if (titulo) endpoint += '&name=' + encodeURIComponent(titulo);
      porProducto[productId] = pedirJson(endpoint).catch(function (err) {
        avisar(endpoint, err.message);
        // Se propaga como marca de error para que el montaje lo distinga de
        // "no hay subastas". Son el mismo pixel y no la misma cosa.
        throw err;
      });
    }
    return porProducto[productId];
  }

  // --- Modal de puja -------------------------------------------------------

  function cerrarModal(overlay) {
    if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
    document.removeEventListener('keydown', overlay._esc);
  }

  function abrirModal(subasta) {
    var overlay = el('div', 'vault-subastas-overlay');
    var panel = el('div', 'vault-subastas-modal');

    panel.appendChild(el('p', 'vault-subastas-modal__title', 'Pujar por esta carta'));
    panel.appendChild(el('p', 'vault-subastas-modal__card', subasta.title));

    var estado = el('p', 'vault-subastas-modal__estado', 'Confirmando la puja mínima…');
    panel.appendChild(estado);

    var campo = el('input', 'vault-subastas-modal__input');
    campo.type = 'number';
    campo.step = '1';
    campo.disabled = true;
    panel.appendChild(campo);

    var error = el('p', 'vault-subastas-modal__error');
    error.hidden = true;
    panel.appendChild(error);

    var acciones = el('div', 'vault-subastas-modal__acciones');
    var cancelar = el('button', 'vault-subastas-modal__cancelar', 'Cancelar');
    cancelar.type = 'button';
    var enviar = el('button', 'vault-subastas-modal__enviar', 'Pujar');
    enviar.type = 'button';
    enviar.disabled = true;
    acciones.appendChild(cancelar);
    acciones.appendChild(enviar);
    panel.appendChild(acciones);

    overlay.appendChild(panel);
    document.body.appendChild(overlay);

    overlay._esc = function (e) {
      if (e.key === 'Escape') cerrarModal(overlay);
    };
    document.addEventListener('keydown', overlay._esc);
    cancelar.addEventListener('click', function () { cerrarModal(overlay); });
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) cerrarModal(overlay);
    });

    function fallar(mensaje) {
      estado.hidden = true;
      error.hidden = false;
      error.textContent = mensaje;
    }

    // La ÚNICA re-validación del diseño: recalcula el piso de la puja contra el
    // estado real, y es el único momento en que este módulo puede enterarse de
    // que la subasta cerró.
    var endpoint = APP + '/api/auctions/' + encodeURIComponent(subasta.id) + '/can-bid';
    pedirJson(endpoint, { credentials: 'include' })
      .then(function (elegibilidad) {
        if (!elegibilidad.can_bid) {
          if (elegibilidad.reason === 'auction_ended' || elegibilidad.reason === 'auction_not_active') {
            fallar('Esta subasta acaba de cerrar.');
          } else {
            // Cambió algo entre la carga y el clic: la página de la subasta
            // sabe resolverlo (login, verificación de celular).
            window.location.href = APP + '/auctions/' + subasta.id;
          }
          return;
        }

        var minimo = Number(elegibilidad.next_min_bid);
        estado.textContent = 'Puja mínima ' + money(minimo);
        campo.min = String(minimo);
        campo.value = String(minimo);
        campo.disabled = false;
        enviar.disabled = false;
        campo.focus();

        enviar.addEventListener('click', function () {
          var monto = Number(campo.value);
          if (!isFinite(monto) || monto < minimo) {
            fallar('La puja mínima es ' + money(minimo) + '.');
            return;
          }
          enviar.disabled = true;
          enviar.textContent = 'Enviando…';

          fetch(APP + '/api/auctions/' + encodeURIComponent(subasta.id) + '/bids', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ amount: monto })
          })
            .then(function (res) {
              return res.json().then(function (cuerpo) {
                return { ok: res.ok, cuerpo: cuerpo };
              });
            })
            .then(function (r) {
              if (!r.ok) {
                // Aquí el error SÍ se muestra: el comprador acaba de actuar y
                // puede hacer algo al respecto.
                fallar(r.cuerpo && r.cuerpo.error ? r.cuerpo.error : 'No se pudo registrar la puja.');
                enviar.disabled = false;
                enviar.textContent = 'Pujar';
                return;
              }
              // Antes de recargar: el helper manda con `keepalive`, así que el
              // evento sobrevive a que la página se vaya.
              medir('pdp_auction_bid', subasta.id);
              cerrarModal(overlay);
              // La respuesta del módulo quedó vieja en el acto. Recargar es lo
              // honesto y lo barato: la puja ya está registrada.
              window.location.reload();
            })
            .catch(function (err) {
              avisar('POST /bids', err.message);
              fallar('No se pudo registrar la puja.');
              enviar.disabled = false;
              enviar.textContent = 'Pujar';
            });
        });
      })
      .catch(function (err) {
        avisar(endpoint, err.message);
        fallar('No se pudo confirmar la puja. Intenta desde la página de la subasta.');
      });
  }

  // --- Pintado -------------------------------------------------------------

  function pintar(mount, subasta, disponible) {
    var caja = el('div', 'vault-subastas__caja');
    // Una posición, dos presentaciones: discreta cuando la variante se puede
    // comprar, protagónica cuando está agotada — que es el caso COMÚN, porque
    // reservar la única copia para subasta deja agotada esa variante (ADR 151).
    caja.className += disponible ? ' vault-subastas__caja--discreta' : ' vault-subastas__caja--protagonica';

    caja.appendChild(el('p', 'vault-subastas__titulo', '🔨 También en subasta'));

    // La imagen manda a la izquierda y el texto se acomoda a su lado; sin
    // imagen el bloque queda como estaba, sin hueco reservado.
    var arte = miniatura(subasta);
    var cuerpo = el('div', 'vault-subastas__cuerpo');
    if (arte) {
      cuerpo.className += ' vault-subastas__cuerpo--con-arte';
      cuerpo.appendChild(arte);
    }
    var texto = el('div', 'vault-subastas__texto');
    texto.appendChild(el('p', 'vault-subastas__carta', lineaDeCarta(subasta)));

    var fila = el('div', 'vault-subastas__fila');
    var puja = el('span', 'vault-subastas__puja');
    puja.appendChild(el('span', 'vault-subastas__etiqueta', 'Puja actual'));
    puja.appendChild(el('strong', 'vault-subastas__monto', money(subasta.current_price)));
    fila.appendChild(puja);

    var reloj = el('span', 'vault-subastas__reloj');
    reloj.setAttribute('data-vault-subastas-end', subasta.end_date);
    reloj.textContent = 'cierra en ' + restante(subasta.end_date);
    fila.appendChild(reloj);
    texto.appendChild(fila);

    var pct = porcentajeDebajo(subasta.current_price, subasta.list_price);
    if (pct !== null) {
      texto.appendChild(el('p', 'vault-subastas__delta', pct + '% debajo del precio de lista'));
    }

    cuerpo.appendChild(texto);
    caja.appendChild(cuerpo);

    var pie = el('div', 'vault-subastas__pie');
    // `next_min_bid` viene resuelto por la API y NO se recalcula aquí.
    //
    // Este renglón sumaba `current_price + minimum_bid`, y esa cuenta está mal
    // en la primera puja: el primer postor puja AL precio inicial, no por
    // encima. En una subasta que empieza en $50 con incremento de $10, la ficha
    // pedía $60 antes de que nadie hubiera pujado.
    //
    // El fallback existe para las respuestas cacheadas de antes del despliegue
    // de CardVault: con `max-age=30` dura medio minuto, y mientras tanto es
    // mejor la cuenta vieja que un "Puja mínima $NaN".
    var minimo = subasta.next_min_bid !== undefined && subasta.next_min_bid !== null
      ? Number(subasta.next_min_bid)
      : Number(subasta.current_price) + Number(subasta.minimum_bid);
    pie.appendChild(el('span', 'vault-subastas__minima', 'Puja mínima ' + money(minimo)));

    // Ver la subasta completa, SIEMPRE (ADR 004 §1). Antes solo aparecía como
    // consuelo para quien no podía pujar: el que sí podía no tenía forma de
    // llegar al historial de pujas, las notas del vendedor ni el envío. Va como
    // secundaria — pujar sigue siendo la acción principal — y se pinta ya, sin
    // esperar a `can-bid`, porque no depende de quién pregunta.
    var ver = el('a', 'vault-subastas__ver', 'Ver subasta');
    ver.href = APP + '/auctions/' + subasta.id;
    ver.addEventListener('click', function () { medir('pdp_auction_click', 'subasta'); });
    pie.appendChild(ver);

    caja.appendChild(pie);

    mount.appendChild(caja);
    return pie;
  }

  /**
   * El botón depende de quién pregunta, así que el precheck se llama SOLO si
   * hubo match: en un catálogo de miles de fichas con un puñado de subastas
   * vivas, llamarlo en cada carga sería gastar una petición autenticada para no
   * mostrar nada casi siempre.
   */
  function ponerBoton(pie, subasta) {
    var endpoint = APP + '/api/auctions/' + encodeURIComponent(subasta.id) + '/can-bid';
    if (!elegibilidadPorSubasta[subasta.id]) {
      elegibilidadPorSubasta[subasta.id] = pedirJson(endpoint, { credentials: 'include' });
    }
    elegibilidadPorSubasta[subasta.id]
      .then(function (elegibilidad) {
        var reason = elegibilidad.reason;

        // Sin botón: no hay nada que ofrecerle a esta persona en esta subasta.
        if (reason === 'own_auction' || reason === 'auction_ended' ||
            reason === 'auction_not_active' || reason === 'not_found') {
          return;
        }

        if (elegibilidad.can_bid) {
          var boton = el('button', 'vault-subastas__btn', 'Pujar');
          boton.type = 'button';
          boton.addEventListener('click', function () {
            medir('pdp_auction_click', 'pujar');
            abrirModal(subasta);
          });
          pie.appendChild(boton);
          return;
        }

        // `unauthenticated` y `phone_unverified`: los dos flujos ya viven en la
        // página de la subasta (el gate de login y el modal de verificación de
        // ADR 052 se disparan solos). El PDP solo decide a dónde mandar.
        var enlace = el('a', 'vault-subastas__btn', 'Pujar');
        enlace.href = APP + '/auctions/' + subasta.id;
        // `subasta` y no `pujar`: a esta persona la sesión o la verificación de
        // celular le impidieron pujar aquí. Separarlos es lo que vuelve medible
        // el costo de esa puerta.
        enlace.addEventListener('click', function () { medir('pdp_auction_click', 'subasta'); });
        pie.appendChild(enlace);
      })
      .catch(function (err) {
        // Sin precheck no se pinta botón: ofrecer "Pujar" sin saber si puede
        // termina en un rechazo después del clic.
        avisar(endpoint, err.message);
      });
  }

  function hidratar(mount) {
    if (mount.dataset.vaultSubastasDone) return;
    mount.dataset.vaultSubastasDone = '1';

    var productId = mount.getAttribute('data-product-id');
    if (!productId) return;
    var disponible = mount.getAttribute('data-variant-available') === 'true';
    // Ausente si el producto no es un single de Magic: el Liquid decide.
    var titulo = mount.getAttribute('data-product-title');

    subastasDe(productId, titulo)
      .then(function (data) {
        var subastas = (data && data.auctions) || [];
        if (subastas.length === 0) {
          // El estado vacío es NO renderizar: nunca un módulo vacío ni un "no
          // hay subastas de esta carta", que solo añade ruido a miles de
          // páginas. Es lo esperado, no un fallo.
          marcar(mount, 'empty');
          return;
        }

        // La que cierra primero es la que urge y la única que se pinta: el
        // módulo habla de una subasta, no de un listado.
        subastas.sort(function (a, b) {
          return new Date(a.end_date) - new Date(b.end_date);
        });

        var pie = pintar(mount, subastas[0], disponible);
        mount.style.display = '';
        marcar(mount, 'ok');
        // Se mide la que se PINTA, no todas las que empataron: es la única que
        // el comprador puede ver y la única sobre la que puede actuar.
        if (!vistasReportadas[subastas[0].id]) {
          vistasReportadas[subastas[0].id] = true;
          medir('pdp_auction_view', subastas[0].id);
        }
        ponerBoton(pie, subastas[0]);
      })
      .catch(function () {
        // Ya se avisó por consola en `subastasDe`. Al comprador, nada.
        marcar(mount, 'error');
      });
  }

  function hidratarTodos() {
    document.querySelectorAll('[data-vault-subastas]').forEach(hidratar);
  }

  // Un solo reloj para todos los contadores vivos. Sobrevive al re-montaje
  // porque busca en el DOM en cada tick en vez de guardar referencias.
  setInterval(function () {
    document.querySelectorAll('[data-vault-subastas-end]').forEach(function (nodo) {
      nodo.textContent = 'cierra en ' + restante(nodo.getAttribute('data-vault-subastas-end'));
    });
  }, 1000);

  // Dawn reemplaza la zona de `buy_buttons` al cambiar de variante y se lleva
  // el módulo montado. Este script vive FUERA de esa zona —se carga en el
  // layout— así que persiste y vuelve a hidratar lo que reaparezca. Es el mismo
  // problema que `reference-price.liquid` resolvió partiéndose en markup+script.
  if (window.MutationObserver) {
    var pendiente = false;
    new MutationObserver(function () {
      if (pendiente) return;
      pendiente = true;
      window.requestAnimationFrame(function () {
        pendiente = false;
        hidratarTodos();
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hidratarTodos);
  } else {
    hidratarTodos();
  }
})();
